<template>
  <div class="profile-page max-w-700 p-40 small:p-20">
    <!-- Header: photo (Firebase Auth photoURL, also shown in the iOS app), name and email -->
    <div class="flex items-center mb-30">
      <div class="profile-photo relative flex-shrink-0">
        <button
          type="button"
          :title="userAuthData.guestPhotoUrl ? 'Change profile photo' : 'Upload a profile photo'"
          :aria-label="userAuthData.guestPhotoUrl ? 'Change profile photo' : 'Upload a profile photo'"
          :disabled="photoIsUploading"
          class="block rounded-full focus:outline-none focus:shadow-outline"
          @click="$refs['photo-input'].click()"
        >
          <AvatarChip
            :initials="userInitials"
            :name-length="(userAuthData.displayName || '').length"
            :src="userAuthData.guestPhotoUrl"
            background-color="#333333"
            color="#f8f8f8"
            :size="96"
            class="text-3xl"
          />
          <span
            v-if="photoIsUploading"
            class="absolute inset-0 flex items-center justify-center rounded-full"
            style="background-color: rgba(0, 0, 0, 0.45);"
          >
            <FontAwesomeIcon
              :icon="['fal', 'spinner-third']"
              spin
              class="text-3xl text-white"
            />
          </span>
          <span class="photo-badge photo-badge-edit absolute bottom-0 right-0">
            <FontAwesomeIcon :icon="['fas', 'camera']" />
          </span>
        </button>
        <button
          v-if="hasUploadedPhoto && !photoIsUploading"
          type="button"
          title="Remove profile photo"
          aria-label="Remove profile photo"
          class="photo-badge photo-badge-remove absolute top-0 right-0 focus:outline-none"
          @click="onRemoveProfilePhotoPress"
        >
          <FontAwesomeIcon :icon="['far', 'times']" />
        </button>
        <input
          ref="photo-input"
          type="file"
          accept="image/*"
          class="hidden"
          @change="onProfilePhotoSelected"
        >
      </div>
      <div class="ml-20 min-w-0">
        <p class="font-bold text-xl text-primary leading-tight truncate">
          {{ userAuthData.displayName || 'Your profile' }}
        </p>
        <p class="text-sm text-primary-light leading-snug truncate mt-2">{{ userAuthData.email }}</p>
        <p class="text-xs text-primary-light leading-snug mt-6">
          Tap the photo to {{ userAuthData.guestPhotoUrl ? 'change' : 'upload' }} it. Shown in the web and iOS apps.
        </p>
      </div>
    </div>

    <!-- Account details (read-only, each field edited in a dialog) -->
    <h2 class="section-title">Account details</h2>
    <ul class="details-card">
      <li
        v-for="row in detailRows"
        :key="row.key"
        class="details-row"
      >
        <div class="min-w-0 flex-1">
          <p class="details-label">{{ row.label }}</p>
          <p
            class="details-value truncate"
            :class="{ 'is-empty': row.isEmpty }"
            :data-private="row.private ? '' : null"
          >
            {{ row.value }}
          </p>
          <div
            v-if="row.key === 'email'"
            class="email-verification flex flex-wrap items-center text-sm mt-6"
            :class="userAuthData.emailVerified ? 'is-verified' : 'is-unverified'"
          >
            <span class="status-chip inline-flex items-center font-medium text-xs py-5 px-8 mr-10 rounded">
              <FontAwesomeIcon
                :icon="userAuthData.emailVerified ? ['fas', 'check-circle'] : ['fas', 'exclamation-triangle']"
                class="mr-6"
              />
              {{ userAuthData.emailVerified ? 'Verified' : 'Not verified' }}
            </span>
            <template v-if="!userAuthData.emailVerified">
              <button
                type="button"
                class="link-button"
                :disabled="verificationIsSending || verificationSentRecently"
                @click="onResendVerificationPress"
              >
                {{ verificationSentRecently ? 'Verification email sent' : 'Resend verification email' }}
              </button>
              <button
                type="button"
                class="link-button text-primary-light ml-12"
                @click="onRefreshVerificationPress"
              >
                I have verified it
              </button>
            </template>
          </div>
          <p
            v-if="row.hint"
            class="text-xs text-primary-light leading-snug mt-4"
          >
            {{ row.hint }}
          </p>
        </div>
        <ElButton
          v-if="row.editable"
          circle
          :title="row.actionTitle"
          :aria-label="row.actionTitle"
          class="edit-button flex-shrink-0"
          @click="openDialog(row.dialog)"
        >
          <FontAwesomeIcon :icon="['fas', 'pen']" />
        </ElButton>
      </li>
    </ul>

    <!-- Sign-in methods -->
    <h2 class="section-title mt-40">Sign-in methods</h2>
    <p class="text-sm text-primary-light leading-snug mb-15">
      Link your Google, Facebook or Apple account to sign in with it.
    </p>
    <div class="flex flex-wrap">
      <ElButton
        type="primary"
        class="signin-google mr-10 mb-10"
        :disabled="userAuthData.providers.length === 1 && userProvidersIncludesGoogle"
        @click="onAuthProviderPress('google.com')"
      >
        <FontAwesomeIcon
          :icon="['fab', 'google']"
          size="lg"
          class="mr-15"
        />
        <template v-if="!userProvidersIncludesGoogle">Link with Google</template>
        <template v-else-if="userAuthData.providers.length === 1">Linked with Google</template>
        <template v-else>Unlink Google</template>
      </ElButton>
      <ElButton
        type="primary"
        class="signin-facebook mr-10 mb-10"
        :disabled="userAuthData.providers.length === 1 && userProvidersIncludesFacebook"
        @click="onAuthProviderPress('facebook.com')"
      >
        <FontAwesomeIcon
          :icon="['fab', 'facebook-f']"
          size="lg"
          class="mr-15"
        />
        <template v-if="!userProvidersIncludesFacebook">Link with Facebook</template>
        <template v-else-if="userAuthData.providers.length === 1">Linked with Facebook</template>
        <template v-else>Unlink Facebook</template>
      </ElButton>
      <ElButton
        type="primary"
        class="signin-apple mb-10"
        :disabled="userAuthData.providers.length === 1 && userProvidersIncludesApple"
        @click="onAuthProviderPress('apple.com')"
      >
        <FontAwesomeIcon
          :icon="['fab', 'apple']"
          size="lg"
          class="mr-15"
        />
        <template v-if="!userProvidersIncludesApple">Link with Apple</template>
        <template v-else-if="userAuthData.providers.length === 1">Linked with Apple</template>
        <template v-else>Unlink Apple</template>
      </ElButton>
    </div>

    <p class="text-xs text-primary-light text-right mt-40">
      {{ `Eventor v. ${appVersion}` }}
    </p>

    <!-- Edit dialog -->
    <ElDialog
      :visible="dialog.visible"
      width="100%"
      custom-class="profile-edit-dialog"
      :close-on-click-modal="!dialog.saving"
      :show-close="!dialog.saving"
      append-to-body
      @close="closeDialog"
      @opened="focusFirstField"
    >
      <p
        slot="title"
        class="font-bold text-lg text-primary truncate pr-20"
      >
        {{ dialogConfig.title }}
      </p>
      <p
        v-if="dialogConfig.description"
        class="text-sm text-primary-light leading-snug mb-15"
      >
        {{ dialogConfig.description }}
      </p>
      <ElForm
        ref="dialog-form"
        :model="dialog.form"
        :rules="dialogConfig.rules"
        label-position="top"
        @submit.native.prevent="onDialogSave"
      >
        <ElFormItem
          v-for="(field, index) in dialogConfig.fields"
          :key="field.prop"
          :label="field.label"
          :prop="field.prop"
          :error="dialog.errors[field.prop]"
        >
          <ElInput
            :ref="index === 0 ? 'first-field' : null"
            v-model="dialog.form[field.prop]"
            :type="field.type || 'text'"
            :show-password="field.type === 'password'"
            :placeholder="field.placeholder"
            :autocomplete="field.autocomplete"
            @input="dialog.errors[field.prop] = ''"
            @keyup.enter.native="onDialogSave"
          />
        </ElFormItem>
      </ElForm>
      <div
        slot="footer"
        class="flex items-center justify-end"
      >
        <ElButton
          type="text"
          :disabled="dialog.saving"
          class="mr-15"
          @click="closeDialog"
        >
          Cancel
        </ElButton>
        <ElButton
          type="primary"
          :loading="dialog.saving"
          @click="onDialogSave"
        >
          Save
        </ElButton>
      </div>
    </ElDialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { FBFirestoreRef } from '@/helpers/firebaseServices';
import { showErrorNotification, showSuccessNotification } from '@/utils/notification';
import { normalizePhoneNumberOrKeep, phoneNumberIsAcceptable } from '@/utils/phone';
import AvatarChip from '@/components/AvatarChip';

// Phone lives on users/{uid}.phone and company on users/{uid}/billing/details.company, the same documents the
// iOS profile screen reads and writes
const userDoc = (uid) => FBFirestoreRef.collection('users').doc(uid);
const billingDoc = (uid) => userDoc(uid).collection('billing').doc('details');

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const required = (message) => ({
  required: true, whitespace: true, message, trigger: 'blur',
});
const minPassword = { min: 6, message: 'Use at least 6 characters.', trigger: 'blur' };

export default {
  components: {
    AvatarChip,
  },
  data: () => ({
    appVersion: process.env.VUE_APP_VERSION,
    photoIsUploading: false,
    verificationIsSending: false,
    verificationSentRecently: false,
    details: {
      phone: '',
      company: '',
    },
    dialog: {
      visible: false,
      type: '',
      saving: false,
      form: {},
      errors: {},
    },
  }),
  created() {
    // Pick up a verification that was completed in the email link (other tab/device)
    if (!this.userAuthData.emailVerified) {
      this.refreshEmailVerified().catch((error) => console.error(error));
    }
    this.loadDetails();
  },
  computed: {
    ...mapState('user/auth', {
      userAuthData: 'authData',
    }),
    userProvidersIncludesPassword() {
      return this.userAuthData.providers.includes('password');
    },
    userProvidersIncludesGoogle() {
      return this.userAuthData.providers.includes('google.com');
    },
    userProvidersIncludesFacebook() {
      return this.userAuthData.providers.includes('facebook.com');
    },
    userProvidersIncludesApple() {
      return this.userAuthData.providers.includes('apple.com');
    },
    // Only photos uploaded here can be removed (not Google/Facebook avatars)
    hasUploadedPhoto() {
      const url = this.userAuthData.guestPhotoUrl || '';
      return Boolean(this.userAuthData.id) && url.includes(encodeURIComponent(`users/${this.userAuthData.id}/profile`));
    },
    userInitials() {
      const parts = (this.userAuthData.displayName || '').trim().split(/\s+/);
      return ((parts[0] || '').charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '')).toUpperCase();
    },
    detailRows() {
      const hasPassword = this.userProvidersIncludesPassword;
      return [{
        key: 'name',
        label: 'Name',
        value: this.userAuthData.displayName || 'Not set',
        isEmpty: !this.userAuthData.displayName,
        editable: true,
        dialog: 'name',
        actionTitle: 'Edit name',
        private: true,
      }, {
        key: 'email',
        label: 'Email',
        value: this.userAuthData.email || 'Not set',
        isEmpty: !this.userAuthData.email,
        editable: hasPassword,
        dialog: 'email',
        actionTitle: 'Change email',
        hint: hasPassword ? '' : 'Your email comes from your Google/Facebook sign-in.',
        private: true,
      }, {
        key: 'password',
        label: 'Password',
        value: hasPassword ? '••••••••' : 'Not set',
        isEmpty: !hasPassword,
        editable: true,
        dialog: hasPassword ? 'password' : 'setPassword',
        actionTitle: hasPassword ? 'Change password' : 'Set a password',
        hint: hasPassword ? '' : 'Add a password to also sign in with your email address.',
      }, {
        key: 'phone',
        label: 'Phone',
        value: this.details.phone || 'Not set',
        isEmpty: !this.details.phone,
        editable: true,
        dialog: 'phone',
        actionTitle: 'Edit phone',
        private: true,
      }, {
        key: 'company',
        label: 'Company',
        value: this.details.company || 'Not set',
        isEmpty: !this.details.company,
        editable: true,
        dialog: 'company',
        actionTitle: 'Edit company',
      }];
    },
    dialogConfig() {
      const currentPassword = {
        prop: 'currentPassword', label: 'Current password', type: 'password', autocomplete: 'current-password',
      };
      const configs = {
        name: {
          title: 'Edit name',
          fields: [{
            prop: 'displayName', label: 'Name', placeholder: 'Joe Doe', autocomplete: 'name',
          }],
          rules: { displayName: [required('Please enter your name.')] },
        },
        email: {
          title: 'Change email',
          description: 'Enter your new email and confirm with your current password. We will send a verification link to '
            + 'the new address.',
          fields: [
            {
              prop: 'email', label: 'New email', placeholder: 'name@example.com', autocomplete: 'email',
            },
            currentPassword,
          ],
          rules: {
            email: [required('Please enter an email address.'), {
              pattern: emailPattern, message: 'Please enter a valid email address.', trigger: 'blur',
            }],
            currentPassword: [required('Please enter your current password.')],
          },
        },
        password: {
          title: 'Change password',
          fields: [
            currentPassword,
            {
              prop: 'newPassword', label: 'New password', type: 'password', autocomplete: 'new-password',
            },
          ],
          rules: {
            currentPassword: [required('Please enter your current password.')],
            newPassword: [required('Please enter a new password.'), minPassword],
          },
        },
        setPassword: {
          title: 'Set a password',
          description: 'You can then sign in with this email and password in addition to Google/Facebook.',
          fields: [
            {
              prop: 'email', label: 'Email', placeholder: 'name@example.com', autocomplete: 'email',
            },
            {
              prop: 'newPassword', label: 'Password', type: 'password', autocomplete: 'new-password',
            },
          ],
          rules: {
            email: [required('Please enter an email address.'), {
              pattern: emailPattern, message: 'Please enter a valid email address.', trigger: 'blur',
            }],
            newPassword: [required('Please enter a password.'), minPassword],
          },
        },
        phone: {
          title: 'Edit phone',
          fields: [{
            prop: 'phone', label: 'Phone', type: 'tel', placeholder: '+41 79 123 45 67', autocomplete: 'tel',
          }],
          rules: {
            phone: [{
              pattern: /^[+()\d\s./-]{0,30}$/, message: 'Please use digits, spaces and + ( ) - only.', trigger: 'blur',
            }, {
              validator: (rule, value, callback) => (phoneNumberIsAcceptable(value)
                ? callback() : callback('Enter a valid phone number, e.g. +41 79 123 45 67')),
              trigger: 'blur',
            }],
          },
        },
        company: {
          title: 'Edit company',
          fields: [{
            prop: 'company', label: 'Company', placeholder: 'Company name', autocomplete: 'organization',
          }],
          rules: {},
        },
      };
      return configs[this.dialog.type] || { title: '', fields: [], rules: {} };
    },
  },
  methods: {
    ...mapActions('user/auth', {
      updateProfile: 'updateProfile',
      updateEmail: 'updateEmail',
      updatePassword: 'updatePassword',
      linkWithCredential: 'linkWithCredential',
      reauthenticateWithCredential: 'reauthenticateWithCredential',
      sendEmailVerification: 'sendEmailVerification',
      linkAccountWithProvider: 'linkAccountWithProvider',
      unlinkAccountWithProvider: 'unlinkAccountWithProvider',
      uploadProfilePhoto: 'uploadProfilePhoto',
      removeProfilePhoto: 'removeProfilePhoto',
      refreshEmailVerified: 'refreshEmailVerified',
    }),
    async loadDetails() {
      const uid = this.userAuthData.id;
      if (!uid) return;
      const [user, billing] = await Promise.all([
        userDoc(uid).get().catch((error) => { console.error(error); return null; }),
        billingDoc(uid).get().catch((error) => { console.error(error); return null; }),
      ]);
      this.details.phone = (user && user.exists && user.data().phone) || '';
      this.details.company = (billing && billing.exists && billing.data().company) || '';
    },
    openDialog(type) {
      const values = {
        name: { displayName: this.userAuthData.displayName || '' },
        email: { email: '', currentPassword: '' },
        password: { currentPassword: '', newPassword: '' },
        setPassword: { email: this.userAuthData.email || '', newPassword: '' },
        phone: { phone: this.details.phone },
        company: { company: this.details.company },
      };
      this.dialog = {
        visible: true, type, saving: false, form: { ...values[type] }, errors: {},
      };
      this.$nextTick(() => this.$refs['dialog-form'] && this.$refs['dialog-form'].clearValidate());
    },
    closeDialog() {
      if (this.dialog.saving) return;
      this.dialog.visible = false;
    },
    focusFirstField() {
      const [first] = [].concat(this.$refs['first-field'] || []);
      if (first) first.focus();
    },
    async onDialogSave() {
      if (this.dialog.saving) return;
      try {
        await this.$refs['dialog-form'].validate();
      } catch (invalid) {
        return;
      }

      const { type, form } = this.dialog;
      this.dialog.saving = true;
      try {
        if (type === 'name') {
          await this.updateProfile(form.displayName.trim());
          showSuccessNotification('Your name has been updated.');
        } else if (type === 'email') {
          const email = form.email.trim();
          if (email.toLowerCase() === (this.userAuthData.email || '').toLowerCase()) {
            this.dialog.errors = { email: 'This is already your email address.' };
            return;
          }
          if (!await this.reauthenticate(form.currentPassword)) return;
          await this.updateEmail(email);
          this.sendVerificationQuietly();
          showSuccessNotification(`Your email was changed to ${email}. Please verify it with the link we sent you.`, 7000);
        } else if (type === 'password') {
          if (!await this.reauthenticate(form.currentPassword)) return;
          await this.updatePassword(form.newPassword);
          showSuccessNotification('Your password has been changed.');
        } else if (type === 'setPassword') {
          await this.linkWithCredential({ email: form.email.trim(), password: form.newPassword });
          this.sendVerificationQuietly();
          showSuccessNotification('Your password has been set. You can now also sign in with your email.', 6000);
        } else if (type === 'phone') {
          // Stored in E.164 so the mobile apps can dial and match it.
          const phone = normalizePhoneNumberOrKeep(form.phone) || '';
          await userDoc(this.userAuthData.id).set({ phone }, { merge: true });
          this.details.phone = phone;
          showSuccessNotification('Your phone number has been updated.');
        } else if (type === 'company') {
          const company = (form.company || '').trim();
          await billingDoc(this.userAuthData.id).set({ company }, { merge: true });
          this.details.company = company;
          showSuccessNotification('Your company has been updated.');
        }
        this.dialog.saving = false;
        this.dialog.visible = false;
      } catch (error) {
        console.error(error);
        const messages = {
          'auth/email-already-in-use': 'This email address is already used by another account.',
          'auth/invalid-email': 'Please enter a valid email address.',
          'auth/weak-password': 'This password is too weak, please choose a longer one.',
          'auth/credential-already-in-use': 'This email is already linked to another account.',
          'auth/requires-recent-login': 'For security reasons please sign out and sign in again, then retry.',
          'permission-denied': 'You are not allowed to change this field right now.',
        };
        showErrorNotification(messages[error.code] || `Your change couldn't be saved (${error.message}).`, 6000);
      } finally {
        this.dialog.saving = false;
      }
    },
    // Changing email or password requires a recent sign-in; confirm with the current password first
    async reauthenticate(password) {
      try {
        await this.reauthenticateWithCredential({ email: this.userAuthData.email, password });
        return true;
      } catch (error) {
        const wrongPassword = ['auth/wrong-password', 'auth/invalid-credential'].includes(error.code);
        this.dialog.errors = {
          currentPassword: wrongPassword ? 'The password is not correct.' : (error.message || 'Please try again.'),
        };
        return false;
      }
    },
    sendVerificationQuietly() {
      this.sendEmailVerification().catch((error) => console.error(error));
    },
    async onProfilePhotoSelected(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      this.$refs['photo-input'].value = ''; // allow picking the same file again
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        showErrorNotification('Please choose an image file (JPG or PNG).');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        showErrorNotification('This image is too large, please choose one under 15 MB.');
        return;
      }

      this.photoIsUploading = true;
      try {
        await this.uploadProfilePhoto(file);
        showSuccessNotification('Your profile photo has been updated.');
      } catch (error) {
        console.error(error);
        showErrorNotification(`Your profile photo couldn't be uploaded (${error.message}).`);
      } finally {
        this.photoIsUploading = false;
      }
    },
    async onRemoveProfilePhotoPress() {
      this.photoIsUploading = true;
      try {
        await this.removeProfilePhoto();
        showSuccessNotification('Your profile photo has been removed.');
      } catch (error) {
        console.error(error);
        showErrorNotification(`Your profile photo couldn't be removed (${error.message}).`);
      } finally {
        this.photoIsUploading = false;
      }
    },
    async onResendVerificationPress() {
      this.verificationIsSending = true;
      try {
        await this.sendEmailVerification();
        this.verificationSentRecently = true;
        setTimeout(() => { this.verificationSentRecently = false; }, 60000);
        showSuccessNotification(`We sent a verification link to ${this.userAuthData.email}. `
          + 'If you do not find it in your inbox, please also check your Junk/Spam folder.', 7000);
      } catch (error) {
        console.error(error);
        const message = error.code === 'auth/too-many-requests'
          ? 'Too many verification emails were requested. Please wait a few minutes and try again.'
          : `The verification email couldn't be sent (${error.message}).`;
        showErrorNotification(message, 7000);
      } finally {
        this.verificationIsSending = false;
      }
    },
    async onRefreshVerificationPress() {
      try {
        const verified = await this.refreshEmailVerified();
        if (verified) showSuccessNotification('Thanks, your email address is verified.');
        else showErrorNotification('Your email address is not verified yet. Please open the link in the email.');
      } catch (error) {
        console.error(error);
        showErrorNotification(error.message);
      }
    },
    onAuthProviderPress(providerId) {
      const providers = {
        'google.com': { name: 'Google', linked: this.userProvidersIncludesGoogle },
        'facebook.com': { name: 'Facebook', linked: this.userProvidersIncludesFacebook },
        'apple.com': { name: 'Apple', linked: this.userProvidersIncludesApple },
      };
      const providerName = providers[providerId].name;
      const isLinked = providers[providerId].linked;

      const action = isLinked ? this.unlinkAccountWithProvider(providerId) : this.linkAccountWithProvider(providerId);
      action
        .then(() => {
          showSuccessNotification(`Your account was ${isLinked ? 'unlinked from' : 'linked with'} your ${providerName} account.`);
        })
        .catch((error) => {
          console.error(error);
          showErrorNotification(`Your ${providerName} account couldn't be ${isLinked ? 'unlinked' : 'linked'}. ${error.message}`, 5500);
        });
    },
  },
};
</script>

<style scoped>
.section-title {
  @apply font-bold text-lg text-primary leading-tight mb-12;
}

.details-card {
  @apply bg-white border border-gray-300 rounded-8 overflow-hidden;
}

.details-row {
  @apply flex items-center py-15 px-20;

  & + & {
    @apply border-t border-gray-200;
  }
}

.details-label {
  @apply text-xs text-primary-light uppercase tracking-wider leading-tight mb-4;
}

.details-value {
  @apply text-base text-primary leading-snug;

  &.is-empty {
    @apply text-gray-500;

    font-style: italic;
  }
}

.edit-button {
  @apply text-primary ml-15;
}

.link-button {
  @apply font-medium text-sm text-secondary-dark leading-tight;

  &:hover:not(:disabled) {
    @apply underline;
  }

  &:disabled {
    @apply text-gray-500 cursor-default;
  }
}

.profile-photo .photo-badge {
  @apply h-30 w-30 flex items-center justify-center text-sm rounded-full border-2 border-white shadow-2dp;
}

.profile-photo .photo-badge-edit {
  @apply text-white bg-secondary;
}

.profile-photo .photo-badge-remove {
  @apply text-white bg-primary;

  &:hover,
  &:focus {
    @apply bg-danger;
  }
}

.email-verification.is-verified .status-chip {
  @apply text-status-checked-in bg-status-checked-in-15;
}

.email-verification.is-unverified .status-chip {
  @apply text-warning bg-gray-200;
}

.signin-google {
  @apply bg-brand-google border-brand-google;

  &:hover, &:focus {
    @apply bg-brand-google border-brand-google opacity-75;
  }

  &.is-disabled {
    @apply opacity-50;
  }
}

.signin-apple {
  @apply bg-brand-apple border-brand-apple ml-0;

  &:hover, &:focus {
    @apply bg-brand-apple border-brand-apple opacity-75;
  }

  &.is-disabled {
    @apply opacity-50;
  }
}

.signin-facebook {
  @apply bg-brand-facebook border-brand-facebook ml-0;

  &:hover, &:focus {
    @apply bg-brand-facebook border-brand-facebook opacity-75;
  }

  &.is-disabled {
    @apply opacity-50;
  }
}
</style>

<style>
/* Dialog is appended to <body>, so it cannot use scoped styles */
.profile-edit-dialog {
  max-width: 440px;
  width: calc(100% - 32px) !important;
}
</style>
